import mapboxgl, { MapMouseEvent } from 'mapbox-gl';
import { Coordinate } from '@shared/models/geography.model';
import { Point2D } from '@shared/models/geometry.model';
import { Storey } from '@shared/models/design/storey.model';
import {
  getOffset,
  getDistance,
  toCoordinate,
} from '@shared/helpers/geometry/coordinates.helpers';
import { toPoint2D } from '@shared/helpers/geometry/geometry.helpers';
import {
  projectLngLatAtElevation,
  screenToLngLatAtElevation,
} from '../helpers/map.helpers';
import {
  getEventPosition,
  wrapPreventDefault,
} from '../helpers/map-observables.helpers';
import {
  LineClickPayload,
  PointMeta,
  PointPayload,
} from '../models/map-observables.models';
import { LineInsertResult } from './vertex-interaction.helpers';
import type { PointDragModifiers } from '../helpers/point-drag-modifiers.helpers';

export type BuildPointPayloadArgs = {
  modifiers?: PointDragModifiers;
  event: MapMouseEvent;
  meta: PointMeta;
  originPoint: Point2D;
  map: mapboxgl.Map | undefined;
  location: Coordinate;
  selectedStoreyId: Storey['id'] | undefined;
};

/**
 * Storey that owns an in-flight point or segment drag.
 *
 * Mousedown captures {@link PointPayload.storeyId}. Mouseup can run after a
 * building deselect or a camera URL update cleared the live selection. The
 * continuation must keep the captured storey; reading only the live selection
 * throws "Selected storey ID is required".
 */
export const resolvePointDragStoreyId = (input: {
  dragStoreyId: Storey['id'] | undefined;
  selectedStoreyId: Storey['id'] | undefined;
}): Storey['id'] | undefined =>
  // Prefer the storey captured at mousedown. Live selection is only the fallback
  // for gestures that never started a drag.
  input.dragStoreyId ?? input.selectedStoreyId;

/**
 * Resolve the complete drag group for snapping and boundary checks.
 * Segment endpoints are internal interaction state and take precedence over
 * visible point selection, which intentionally stays empty during edge drags.
 */
export const resolveDragGroupPointIndexes = (input: {
  isLineSegmentDrag: boolean;
  lineSegmentPointIndexes?: readonly [number, number];
  selectedPointIndexes?: readonly number[];
}): ReadonlySet<number> | undefined => {
  if (input.isLineSegmentDrag && input.lineSegmentPointIndexes) {
    return new Set(input.lineSegmentPointIndexes);
  }
  return input.selectedPointIndexes && input.selectedPointIndexes.length > 1
    ? new Set(input.selectedPointIndexes)
    : undefined;
};

/**
 * Build a {@link PointPayload} from a Mapbox event + point metadata.
 *
 * Henrik: keep payload creation deterministic and isolated from orchestration logic.
 */
export const buildPointPayload = ({
  modifiers,
  event,
  meta,
  originPoint,
  map,
  location,
  selectedStoreyId,
}: BuildPointPayloadArgs): PointPayload => {
  if (!selectedStoreyId) throw new Error('Selected storey ID is required');

  const origin = toPoint2D(originPoint);
  const rawScreenPoint = toPoint2D(event.point ?? map?.project(event.lngLat));
  const position = getEventPosition(event, map, meta.elevation, location);

  const coordinate =
    meta.elevation !== undefined && map
      ? screenToLngLatAtElevation(map, rawScreenPoint, meta.elevation)
      : toCoordinate(event.lngLat);

  const screenPoint =
    coordinate &&
    meta.elevation !== undefined &&
    map &&
    !Number.isNaN(meta.elevation)
      ? toPoint2D(projectLngLatAtElevation(map, coordinate, meta.elevation))
      : rawScreenPoint;

  const offset = getOffset(origin, position);
  const distance = getDistance(origin, position);

  return {
    storeyId: selectedStoreyId,
    preventDefault: wrapPreventDefault(event),
    screenPoint,
    coordinate,
    index: meta.index,
    shape: meta.shape,
    shapeId: meta.shapeId,
    isDraft: meta.isDraft,
    origin,
    position,
    offset,
    distance,
    elevation: meta.elevation,
    isShiftKeyPressed: Boolean((modifiers ?? event.originalEvent).shiftKey),
    isMetaOrCtrlKeyPressed: Boolean(
      (modifiers ?? event.originalEvent).metaKey ||
      (modifiers ?? event.originalEvent).ctrlKey,
    ),
  };
};

export type BuildInsertedPointPayloadArgs = {
  event: MapMouseEvent;
  linePayload: LineClickPayload;
  insertResult: LineInsertResult;
  map: mapboxgl.Map | undefined;
  selectedStoreyId: Storey['id'] | undefined;
  getStoreyAdjustedScreenPoint: (event: MapMouseEvent) => Point2D | undefined;
  placeholderId: string;
};

/**
 * Build a {@link PointPayload} for a newly inserted point (insert-drag start).
 */
export const buildInsertedPointPayload = ({
  event,
  linePayload,
  insertResult,
  map,
  selectedStoreyId,
  getStoreyAdjustedScreenPoint,
  placeholderId,
}: BuildInsertedPointPayloadArgs): PointPayload | undefined => {
  const screenPoint =
    map && !Number.isNaN(linePayload.elevation)
      ? toPoint2D(
          projectLngLatAtElevation(
            map,
            insertResult.coordinate,
            linePayload.elevation,
          ),
        )
      : getStoreyAdjustedScreenPoint(event);

  if (!screenPoint || !selectedStoreyId) return undefined;

  return {
    storeyId: selectedStoreyId,
    preventDefault: wrapPreventDefault(event),
    screenPoint,
    coordinate: insertResult.coordinate,
    index: insertResult.insertIndex,
    shape: insertResult.shape,
    shapeId: insertResult.shapeId,
    isDraft: false,
    origin: insertResult.metricPosition,
    position: insertResult.metricPosition,
    offset: [0, 0] as Point2D,
    distance: 0,
    elevation: linePayload.elevation,
    isInsertDrag: true,
    insertPlaceholderId: placeholderId,
    linkedStoreyLevels: insertResult.linkedStoreyLevels,
    linkedShapeIds: insertResult.linkedShapeIds,
    linkedInsertIndexes: insertResult.linkedInsertIndexes,
    isShiftKeyPressed: Boolean(event.originalEvent.shiftKey),
  };
};
